// DrilldownList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Tooltip, ScrollArea } from '@mantine/core';
import { Avatar } from '../../Avatar/Avatar';
import { NavigationButton } from '../ObjGraphsBubbleHtml/ObjGraphsBubbleHtmlCSS';
import ObjectActionCreators from '../../../../actions';
import ObjectApi from '../../../../api/ObjectApi';
import { lighten, isValidColor } from '../../../Utils/colorManipulator';

export default function DrilldownList({
  title,
  objId,
  formulaCat1,
  formulaCat2,
  cat1,
  cat2,
  extraCondition,
  pageSize = 25,
  profiles = {},
  onBack,
  cat1Meta = {}, // { [value]: {display, color, icon} }
  cat2Meta = {}, // idem
  cat1Label = 'Cat1',
  cat2Label = 'Cat2',
  formatValue = (v) => String(v),
}) {
  const dispatch = useDispatch();
  const [items, setItems] = useState([]); // [{id,name}]
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const pretty = (meta, v) => meta?.[v]?.display ?? formatValue(v);
  const color = (meta, v) => meta?.[v]?.color ?? undefined;
  const icon = (meta, v) => meta?.[v]?.icon ?? undefined;

  const maxPage = useMemo(
    () => Math.max(0, Math.ceil((total || 0) / pageSize) - 1),
    [total, pageSize]
  );

  const load = async (p) => {
    setLoading(true);
    setErr(null);
    try {
      // 1) Normalisation RAW/null
      const toRawOrNull = (v) =>
        v === undefined || v === null || v === '' || v === 'null' ? null : v;
      const cat1Raw = toRawOrNull(cat1);
      const cat2Raw = toRawOrNull(cat2);

      // 2) Garde-fou: si on a des meta, détecter un éventuel display passé par erreur
      const looksLikeDisplay = (meta, v) => {
        if (!v || typeof v !== 'string') return false;
        // si "v" correspond à un display d'une entrée meta dont la key RAW est différente,
        // on suspecte un display à la place du raw.
        const entry = Object.entries(meta).find(([raw, m]) => m?.display === v);
        return entry && entry[0] !== v; // la clé RAW != label affiché
      };
      if (looksLikeDisplay(cat1Meta, cat1Raw)) {
        // log non bloquant pour repérer vite d'où ça vient
        console.warn(
          '[DrilldownList] cat1 semble être un DISPLAY, pas un RAW:',
          cat1Raw
        );
      }
      if (looksLikeDisplay(cat2Meta, cat2Raw)) {
        console.warn(
          '[DrilldownList] cat2 semble être un DISPLAY, pas un RAW:',
          cat2Raw
        );
      }
      const data = await ObjectApi.fetchDrilldown(
        objId,
        formulaCat1,
        formulaCat2,
        cat1Raw,
        cat2Raw,
        extraCondition,
        p * pageSize,
        pageSize
      );
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (e) {
      setErr(e.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '2px 8px',
    borderRadius: 5,
    background: '#f3f3f3',
    fontWeight: 'bold',
    fontSize: '12px',
    lineHeight: '16px', // 👈 force une ligne cohérente
    verticalAlign: 'middle', // 👈 centre le badge avec les autres
    cursor: '',
    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
    color: 'var(--ds-text-base)',
  };

  useEffect(() => {
    load(0);
  }, [objId, formulaCat1, formulaCat2, cat1, cat2, extraCondition, pageSize]);

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0, // 👈 ESSENTIEL pour les grids
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        backgroundColor: 'var(--ds-bg-base)',
        borderRadius: '5px',
        padding: '4px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        contain: 'layout', // 👈 ICI
        isolation: 'isolate', // 👈 ICI
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 5,
          marginBottom: 0,
          minWidth: 0,
          width: '100%',
        }}
      >
        {onBack && (
          <div
            onClick={onBack}
            style={{
              ...badgeStyle,
              cursor: 'pointer',
              flexShrink: 0,
              maxWidth: 'none',
            }}
          >
            ←
          </div>
        )}
        <div
          style={{
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {cat1 != null && (
            <span
              style={{
                ...badgeStyle,
                background: lighten(color(cat1Meta, cat1), 0.9) ?? '#f3f3f3',
                marginRight: '5px',
              }}
            >
              {icon(cat1Meta, cat1) && (
                <span
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: '#000',
                    mask: `url('${icon(
                      cat1Meta,
                      cat1
                    )}') no-repeat center/14px 14px`,
                  }}
                />
              )}
              <Tooltip label={cat1Label}> {pretty(cat1Meta, cat1)}</Tooltip>
            </span>
          )}
          {cat2 != null && (
            <span
              style={{
                ...badgeStyle,
                background: lighten(color(cat2Meta, cat2), 0.9) ?? '#f3f3f3',
              }}
            >
              {icon(cat2Meta, cat2) && (
                <span
                  style={{
                    width: 14,
                    height: 14,
                    backgroundColor: '#000',
                    mask: `url('${icon(
                      cat2Meta,
                      cat2
                    )}') no-repeat center/14px 14px`,
                  }}
                />
              )}
              <Tooltip label={cat2Label}> {pretty(cat2Meta, cat2)}</Tooltip>
            </span>
          )}
        </div>
        <div
          style={{
            marginLeft: 'auto',
            backgroundColor: 'var(--ds-base)',
            borderRadius: '5px',
            paddingLeft: '5px',
            paddingRight: '5px',
            height: '16px',
            color: 'var(--ds-text-invert)',
            fontWeight: 'bold',
            fontSize: '10px',
          }}
        >
          {total}
        </div>
      </div>

      {loading && <div>Chargement…</div>}
      {err && <div style={{ color: 'red', maxWidth: '100%' }}>{err}</div>}

      {!loading && !err && (
        <div
          style={{
            width: '100%',
            overflowX: 'hidden',
            maxWidth: '100%',
          }}
        >
          <ScrollArea
            style={{
              border: '1px solid #eee',
              borderRadius: 4,
              maxHeight: 300,
              maxWidth: '100%',
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
            }}
            type="auto" // auto / hover / always
            scrollbarSize={8}
            scrollHideDelay={400}
            styles={{
              viewport: {
                overflowX: 'hidden',
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                contain: 'layout',
              },
            }}
            className="ddScroll"
          >
            <div
              role="list"
              aria-label="Drilldown items"
              style={{
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                paddingBottom: 8,
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              {items.length === 0 ? (
                <div style={{ padding: 12, opacity: 0.7 }}>No records</div>
              ) : (
                items.map(({ id, name }) => (
                  <div
                    key={id}
                    onClick={() =>
                      dispatch(ObjectActionCreators.selectObject(id))
                    }
                    style={{
                      alignItems: 'center',
                      background: 'var(--ds-bg-base)',
                      borderBottom: '1px solid var(--ds-border-base)',
                      borderRadius: '0px',
                      boxSizing: 'border-box',
                      color: 'var(--ds-text-base)',
                      contain: 'layout',
                      cursor: 'pointer',
                      display: 'flex',
                      fontSize: '12px',
                      gap: 8,
                      maxWidth: '100%',
                      minWidth: 0,
                      overflow: 'hidden',
                      padding: '4px 4px',
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    <Avatar objId={id} ratio={0.7} />
                    <div
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontWeight: 500,
                        flex: '1 1 auto', // 👈 prend l'espace restant
                        minWidth: 0, // 👈 nécessaire pour ellipsis en flex
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                        // whiteSpace: 'nowrap', // 👈 nowrap uniquement sur le texte
                      }}
                    >
                      {profiles?.[id]?.name || name || id}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      {maxPage > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 4,
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '11px',
            fontWeight: '500',
            minWidth: 0,
          }}
        >
          <NavigationButton
            onClick={() => load(page - 1)}
            disabled={page <= 0 || loading}
          >
            &lt;
          </NavigationButton>
          <div
            style={{
              flex: '1 1 auto',
              textAlign: 'center',
              minWidth: 0, // 🔧 AJOUT
            }}
          >
            Page {page + 1} / {maxPage + 1}
          </div>
          <NavigationButton
            onClick={() => load(page + 1)}
            disabled={page >= maxPage || loading}
          >
            &gt;
          </NavigationButton>
        </div>
      )}
    </div>
  );
}
